import { useCallback, useEffect, useSyncExternalStore } from 'react'

/**
 * UI scale — the console zoomed as a whole so a 4K panel at 100% OS scaling
 * (3840 CSS px wide, DPR 1) reads like a 2560 one instead of a wall of 8px
 * type. Applied as `--bloo-ui-scale`, consumed by `html { zoom }` in
 * styles.css — zoom scales px values too, which a root font-size cannot
 * (the spacing tokens and bloo-ui components are px).
 *
 * The signal is the CSS-pixel screen width and nothing else: it already
 * folds in OS display scaling and browser zoom. DPR alone cannot tell a
 * 4K-at-1x panel (needs help) from a 4K-at-2x one (already fine), and the
 * browser never reports physical size, so "same inches everywhere" is not
 * computable — "same amount of content per screen" is.
 *
 * `auto` steps rather than slides so user zoom on top does not fight it:
 * <2200 → 1, <3000 → 1.25, else 1.5. The preference is a draft API contract
 * (localStorage `bloo.ui-scale`, see docs/ui-scale.md).
 *
 * THE WINDOW GETS A VETO. The screen says how big the pixels are; it says
 * nothing about how much room the app actually has. A 4K panel with the
 * browser at half width was scaled 1.5 for the panel and left with
 * 2000/1.5 ≈ 1320 layout px — LESS room than a 1440 laptop at 1x, which is
 * how a two-column page ends up wrapping on the biggest monitor in the
 * building (observed 2026-09-21). So the factor is capped at what keeps
 * {@link MIN_LAYOUT_WIDTH} of layout width in the window it is running in.
 */
export type UiScalePref = 'auto' | '1' | '1.25' | '1.5'
export const UI_SCALE_PREFS: readonly UiScalePref[] = ['auto', '1', '1.25', '1.5']
export const UI_SCALE_KEY = 'bloo.ui-scale'

/**
 * The layout width this console needs before it starts giving things up: the
 * rail plus a main column wide enough for the two-column surfaces (the run
 * detail's rail is 60rem-gated; below ~1600 its rows wrap). Zooming past this
 * buys bigger type by taking away the layout it exists to serve.
 */
export const MIN_LAYOUT_WIDTH = 1600

/** The steps, largest first — `auto` picks, the window vetoes. */
const STEPS = [1.5, 1.25, 1] as const

export function autoScale(cssScreenWidth: number, cssWindowWidth?: number): number {
  const wanted = cssScreenWidth >= 3000 ? 1.5 : cssScreenWidth >= 2200 ? 1.25 : 1
  if (cssWindowWidth === undefined || cssWindowWidth <= 0) return wanted
  // The largest step the window can afford, never above what the screen asked
  // for. 1 is the floor: a window too small for the layout at 1x is a window
  // problem, and shrinking type below 1x does not fix it.
  const affordable = STEPS.find((f) => f <= wanted && cssWindowWidth / f >= MIN_LAYOUT_WIDTH)
  return affordable ?? 1
}

export function resolveScale(pref: UiScalePref, cssScreenWidth: number, cssWindowWidth?: number): number {
  return pref === 'auto' ? autoScale(cssScreenWidth, cssWindowWidth) : Number(pref)
}

const listeners = new Set<() => void>()

function read(): UiScalePref {
  const raw = typeof window !== 'undefined' ? window.localStorage.getItem(UI_SCALE_KEY) : null
  return (UI_SCALE_PREFS as readonly string[]).includes(raw ?? '') ? (raw as UiScalePref) : 'auto'
}

function screenWidth(): number {
  return typeof window !== 'undefined' ? window.screen.width : 1920
}

function windowWidth(): number | undefined {
  return typeof window !== 'undefined' ? window.innerWidth : undefined
}

/** One immutable snapshot (pref + effective factor) so a resize that changes
 *  only the factor still re-renders subscribers. */
type Snap = { pref: UiScalePref; factor: number }
let snap: Snap = { pref: read(), factor: 1 }

function apply(pref: UiScalePref) {
  const factor = resolveScale(pref, screenWidth(), windowWidth())
  snap = { pref, factor }
  document.documentElement.style.setProperty('--bloo-ui-scale', String(factor))
}
if (typeof document !== 'undefined') apply(snap.pref)

const subscribe = (cb: () => void) => {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
const notify = () => listeners.forEach((cb) => cb())

export function useUiScale(): [UiScalePref, (p: UiScalePref) => void, number] {
  const { pref, factor } = useSyncExternalStore(subscribe, () => snap)
  // A window dragged to another monitor changes screen.width, and resizing it
  // changes what the layout can afford; `auto` follows both.
  useEffect(() => {
    const onResize = () => {
      if (snap.pref !== 'auto') return
      apply('auto')
      notify()
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const set = useCallback((p: UiScalePref) => {
    window.localStorage.setItem(UI_SCALE_KEY, p)
    apply(p)
    notify()
  }, [])
  return [pref, set, factor]
}
